/**
 * LaunchTab — who can use the app, and the waitlist (Admin → User Management).
 *
 * Writes site_config/launch through `adminSetLaunch`; the client gate
 * (NavigationContext) and the server quota (functions/billing/quota.js) both
 * read it, so a stage change takes effect for searches too, not just screens.
 *   Open   → everyone
 *   Beta   → admins, alpha + beta testers (approving a waitlist entry grants beta)
 *   Closed → admins + alpha testers
 *   Launch date → the gate opens by itself at that moment
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { getLaunchOverview, setLaunch, waitlistAction, importLegacyCaptures } from '../services/launchService';
import { getAdminAuthHeaders } from '../firebaseConfig';
import Constants from 'expo-constants';
import { showToast } from '../utils/toast';
import { A, Pill, Panel, Btn, Segments, EmptyState, Stat, relTime, shortDate } from './admin/adminUi';
import Appear from './motion/Appear';
import { AV, ADM_DISPLAY } from './floodlight/vars/admin';

const STAGE_COPY = {
    open: { title: 'Open', body: 'Anyone can sign up and search.', color: A.green },
    beta: { title: 'Private beta', body: 'Admins, alpha and beta testers. Everyone else sees the Coming Soon page and can join the waitlist.', color: A.blue },
    closed: { title: 'Closed', body: 'Admins and alpha testers only.', color: A.amber },
};
const STATUS_COLOR = { waiting: A.soft, approved: A.green, approved_pending_signup: A.blue, invited: A.accent };

// datetime-local wants local wall time; the doc stores UTC ISO.
const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const functionsBase = () => (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL
    || Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL
);

export default function LaunchTab() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(null);
    const [filter, setFilter] = useState('waiting');
    const [q, setQ] = useState('');

    const load = useCallback(async () => {
        setError(null);
        try {
            const d = await getLaunchOverview();
            setData(d);
            setDraft({ stage: d.launch.stage, launchAt: toLocalInput(d.launch.launchAt), waitlistEnabled: d.launch.waitlistEnabled });
        } catch (e) { setError(e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (draft.stage !== 'open' && Platform.OS === 'web' && !window.confirm(`Switch to ${STAGE_COPY[draft.stage].title}? People without access will be sent to the Coming Soon page and their searches refused.`)) return;
        setSaving(true);
        try {
            await setLaunch({ stage: draft.stage, launchAt: draft.launchAt ? new Date(draft.launchAt).toISOString() : null, waitlistEnabled: draft.waitlistEnabled });
            showToast('Launch settings saved', 'success');
            load();
        } catch (e) { showToast(e.message, 'error'); } finally { setSaving(false); }
    };

    const act = async (entry, action) => {
        setBusy(`${entry.id}:${action}`);
        try {
            if (action === 'invite') {
                const base = functionsBase();
                const res = await fetch(`${base}/adminCreateUserAndInvite`, { method: 'POST', headers: await getAdminAuthHeaders(), body: JSON.stringify({ email: entry.email, role: 'user', planType: 'FREE', betaAccess: true, sendReset: true }) });
                const out = await res.json();
                if (!res.ok) throw new Error(out.error || res.statusText);
                await waitlistAction(entry.id, 'invited');
                showToast(out.emailSent ? `Invite sent to ${entry.email}` : `Account created for ${entry.email} (email not sent)`, 'success');
            } else {
                const out = await waitlistAction(entry.id, action);
                showToast(action === 'approve' ? (out.grantedUid ? 'Approved: beta access granted' : 'Approved: beta is granted when they sign up') : 'Updated', 'success');
            }
            load();
        } catch (e) { showToast(e.message, 'error'); } finally { setBusy(null); }
    };

    const list = useMemo(() => (data?.waitlist || []).filter((w) => (filter === 'all' || (filter === 'waiting' ? w.status === 'waiting' : w.status !== 'waiting'))
        && (!q || String(w.email).toLowerCase().includes(q.toLowerCase()))), [data, filter, q]);
    const counts = useMemo(() => {
        const wl = data?.waitlist || [];
        const week = Date.now() - 7 * 86400000;
        return { total: wl.length, waiting: wl.filter((w) => w.status === 'waiting').length, week: wl.filter((w) => Date.parse(w.createdAt) >= week).length, approved: wl.filter((w) => w.status !== 'waiting').length };
    }, [data]);

    if (error) return <EmptyState icon="!" title="Couldn't load launch settings" body={`${error}. Deploy the launch functions first.`} />;
    if (!data || !draft) return <View style={{ padding: 40, alignItems: 'center' }}><ActivityIndicator color={A.accent} /></View>;
    const dirty = draft.stage !== data.launch.stage || (draft.launchAt || '') !== toLocalInput(data.launch.launchAt) || draft.waitlistEnabled !== data.launch.waitlistEnabled;
    const live = STAGE_COPY[data.launch.stage];

    return (
        <ScrollView contentContainerStyle={{ padding: 16, gap: 16, paddingBottom: 60 }}>
            <View>
                <Text style={{ color: AV.ink, fontSize: 22, fontWeight: '800', ...ADM_DISPLAY }}>Launch & waitlist</Text>
                <Text style={{ color: AV.faint, fontSize: 12, marginTop: 2 }}>
                    Now: <Text style={{ color: live.color, fontWeight: '700' }}>{live.title}</Text>
                    {data.launch.launchAt ? ` · opens ${shortDate(data.launch.launchAt)} (${new Date(data.launch.launchAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})` : ''}
                    {data.launch.source === 'legacy' ? ' · from the old Maintenance setting until you save here' : ''}
                </Text>
            </View>

            <Panel title="Who can use BetOptimized" subtitle="Enforced in the app and on the server (searches are refused without access).">
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                    {Object.entries(STAGE_COPY).map(([key, c], i) => {
                        const on = draft.stage === key;
                        return (
                            <Appear key={key} index={i} style={{ flexGrow: 1, flexBasis: 220 }}>
                                <Btn label={`${on ? '● ' : ''}${c.title}`} tone={on ? 'primary' : 'default'} onPress={() => setDraft((d) => ({ ...d, stage: key }))} accessibilityLabel={`Stage ${c.title}`} />
                                <Text style={{ color: AV.faint, fontSize: 12, marginTop: 6, lineHeight: 17 }}>{c.body}</Text>
                            </Appear>
                        );
                    })}
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                    <View style={{ gap: 4 }}>
                        <Text style={{ color: AV.faint, fontSize: 11, fontWeight: '700' }}>LAUNCH DATE (optional, your local time)</Text>
                        {Platform.OS === 'web' ? (
                            React.createElement('input', {
                                type: 'datetime-local', value: draft.launchAt || '', 'aria-label': 'Launch date',
                                onChange: (e) => setDraft((d) => ({ ...d, launchAt: e.target.value })),
                                style: { background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 10px', colorScheme: 'dark' },
                            })
                        ) : (
                            <TextInput value={draft.launchAt} onChangeText={(v) => setDraft((d) => ({ ...d, launchAt: v }))} placeholder="2026-11-01T09:00" placeholderTextColor={A.faint} style={{ color: AV.ink, borderWidth: 1, borderColor: AV.line, borderRadius: 8, padding: 8 }} />
                        )}
                    </View>
                    {!!draft.launchAt && <Btn small label="Clear date" onPress={() => setDraft((d) => ({ ...d, launchAt: '' }))} />}
                    <Btn small label={draft.waitlistEnabled ? 'Waitlist form: on' : 'Waitlist form: off'} onPress={() => setDraft((d) => ({ ...d, waitlistEnabled: !d.waitlistEnabled }))} />
                    <View style={{ flex: 1 }} />
                    <Btn label={saving ? 'Saving…' : 'Save'} tone="primary" onPress={save} disabled={!dirty || saving} />
                </View>
            </Panel>

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                <Stat index={0} label="Waitlist" value={counts.total} hint={`${counts.week} joined this week`} />
                <Stat index={1} label="Waiting" value={counts.waiting} />
                <Stat index={2} label="Approved / invited" value={counts.approved} tone={counts.approved ? 'good' : undefined} />
                <Stat index={3} label="Old captures" value={data.legacyCaptures} hint="emailCaptures (pre-waitlist)" />
            </View>

            <Panel
                title="Waitlist"
                subtitle="Deduped by email. Approve grants beta access (now, or when they sign up). Invite creates the account and emails a set-password link."
                right={data.legacyCaptures ? <Btn small label="Import old captures" onPress={async () => { try { const r = await importLegacyCaptures(); showToast(`Imported ${r.imported}`, 'success'); load(); } catch (e) { showToast(e.message, 'error'); } }} /> : null}
            >
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <Segments value={filter} onChange={setFilter} options={[{ value: 'waiting', label: 'Waiting' }, { value: 'done', label: 'Approved / invited' }, { value: 'all', label: 'All' }]} />
                    <TextInput value={q} onChangeText={setQ} placeholder="Filter by email" placeholderTextColor={A.faint} accessibilityLabel="Filter waitlist by email"
                        style={{ flex: 1, minWidth: 180, color: AV.ink, backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: AV.line, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 7, fontSize: 13 }} />
                </View>
                {!list.length ? (
                    <EmptyState title={filter === 'waiting' ? 'Nobody waiting' : 'Nothing here'} body="New sign-ups from the Coming Soon page and the Community waitlist land here." />
                ) : list.map((w, i) => (
                    <Appear key={w.id} index={Math.min(i, 10)} distance={6}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, borderTopWidth: 1, borderTopColor: AV.line, flexWrap: 'wrap' }}>
                            <View style={{ flex: 1, minWidth: 220 }}>
                                <Text style={{ color: AV.ink, fontSize: 13, fontWeight: '600' }} selectable>{w.email}</Text>
                                <Text style={{ color: AV.faint, fontSize: 11, marginTop: 2 }}>
                                    joined {relTime(w.createdAt)} · {(w.sources || []).join(', ')}{w.count > 1 ? ` · signed up ${w.count}×` : ''}{w.attribution?.utm_source ? ` · ${w.attribution.utm_source}` : w.attribution?.referrer ? ` · ${w.attribution.referrer.replace(/^https?:\/\//, '').split('/')[0]}` : ''}
                                </Text>
                            </View>
                            <Pill label={String(w.status).replace(/_/g, ' ').toUpperCase()} color={STATUS_COLOR[w.status] || A.soft} />
                            <View style={{ flexDirection: 'row', gap: 6 }}>
                                {w.status === 'waiting' && <Btn small tone="good" label={busy === `${w.id}:approve` ? '…' : 'Approve'} onPress={() => act(w, 'approve')} />}
                                {w.status !== 'invited' && <Btn small label={busy === `${w.id}:invite` ? '…' : 'Invite'} onPress={() => act(w, 'invite')} />}
                                {w.status !== 'waiting' && <Btn small label="Back to waiting" onPress={() => act(w, 'waiting')} />}
                                <Btn small tone="danger" label="Remove" onPress={() => { if (Platform.OS !== 'web' || window.confirm(`Remove ${w.email} from the waitlist?`)) act(w, 'remove'); }} />
                            </View>
                        </View>
                    </Appear>
                ))}
            </Panel>
        </ScrollView>
    );
}
